/**
 * Copyright © 2025 Farmlands Co-operative Society Limited. All rights reserved.
 *
 * This document contains confidential information and is the property of Farmlands.
 * Unauthorised reproduction or distribution of any part of this publication is prohibited without written consent.
 */

import React from 'react';
import { useSelector } from 'react-redux';
import { selectCurrentFarmerAccount, selectHomeBranch } from '../features/azure-b2c/storage';
import { selectStores } from '../features/stores/storage/stores.slice';
import { selectCurrentCart } from '../features/cart/storage/cart.slice';
import { CartDeliveryMode } from '../features/cart/storage/cart.types';
import { selectSeedOrder } from '../features/seed-orders/storage/seedCart.slice';
import { siteToLocationId } from '../features/stores/storage/utils';

export const useBranch = (): string | undefined => {
  const homeBranchId = useSelector(selectHomeBranch);
  const stores = useSelector(selectStores);
  const cart = useSelector(selectCurrentCart);

  const branchId = React.useMemo(() => {
    if (cart?.cartDeliveryMode === CartDeliveryMode.PICKUP) {
      const store = stores.find((x) => x.inventorySiteId === cart?.pickupStore?.inventorySiteId);
      return store?.inventorySiteId ?? homeBranchId;
    } else {
      return homeBranchId;
    }
  }, [cart?.cartDeliveryMode, cart?.pickupStore, stores, homeBranchId]);

  return branchId;
};

export const useBranchLocationId = (): string | undefined => {
  const homeBranchId = useSelector(selectHomeBranch);
  const stores = useSelector(selectStores);
  const cart = useSelector(selectCurrentCart);

  const branchId = React.useMemo(() => {
    const id = cart?.pickupStore?.inventorySiteId ?? homeBranchId;
    const store = stores.find((x) => x.inventorySiteId === id);
    return store?.inventoryLocationId ?? homeBranchId;
  }, [cart?.cartDeliveryMode, cart?.pickupStore, stores, homeBranchId]);

  return branchId;
};

export const useSeedBranch = (seedId?: string) => {
  const homeBranchSiteId = useSelector(selectHomeBranch);
  const account = useSelector(selectCurrentFarmerAccount);
  const accountNumber = account?.accountNumber ?? '';
  const stores = useSelector(selectStores);
  const seedOrder = useSelector(selectSeedOrder(accountNumber));

  const memo = React.useMemo(() => {
    const homeBranchId = siteToLocationId(stores, homeBranchSiteId);
    if (!seedId) {
      return homeBranchId;
    }

    const seed = seedOrder?.mixes.find((x) => x.id === seedId);
    if (!seed) {
      return homeBranchId;
    }

    const storeId = seed.storeId;
    if (!storeId) {
      return homeBranchId;
    }

    const store = stores.find((x) => x.inventoryLocationId === storeId);
    if (store) {
      return store.inventorySiteId;
    } else if (homeBranchId) {
      return homeBranchId;
    } else if (stores.length > 0) {
      return stores[0].inventoryLocationId;
    }
  }, [seedId, homeBranchSiteId, account, stores, seedOrder]);

  return memo;
};
